/**
 * Step2 点击「下一步」时的校验与 error 高亮状态。
 *
 * 错误形态：
 * - `tableFieldErrors`：`source_column` → 单元格字段 key → `true`
 * - `fieldsValueError`：顶部未勾选参与 Cube 的列
 * - `dimSecError` / `measSecError`：内联自定义面板仍打开时区块边框高亮
 *
 * 用户编辑对应控件后会清除单项 error（见各 `clear*` 回调）。
 */
import { useCallback, useState } from 'react';
import { useIntl } from 'umi';
import { notification } from 'tea-component';

import {
  hasFieldTableErrors,
  validateFieldConfigStepOnConfirm,
  type IFieldConfigStepConfirmResult,
  type TFieldTableCellKey,
  type TFieldTableErrors,
} from '../../../utils/fieldConfigRowUtils';
import type {
  IUseFieldConfigStepValidateResult,
  TFieldConfigStepValidateInput,
} from '../types';

const EMPTY_TABLE_ERRORS: TFieldTableErrors = {};

/** 按优先级选取一条校验失败提示（内联未完成 > 表格必填未填） */
const resolveStep2ValidateNotifyMsgId = (
  result: IFieldConfigStepConfirmResult,
): string | undefined => {
  if (result.dimSecError) {
    return 'dbl_model_agent_step2_notify_complete_dim';
  }
  if (result.measSecError) {
    return 'dbl_model_agent_step2_notify_complete_meas';
  }
  if (hasFieldTableErrors(result.cellErrors)) {
    return 'dbl_model_agent_step2_notify_required_fields';
  }
  return undefined;
};

export const useFieldConfigStepValidate = (): IUseFieldConfigStepValidateResult => {
  const { formatMessage } = useIntl();
  const [tableFieldErrors, setTableFieldErrors] =
    useState<TFieldTableErrors>(EMPTY_TABLE_ERRORS);
  const [fieldsValueError, setFieldsValueError] = useState(false);
  const [dimSecError, setDimSecError] = useState(false);
  const [measSecError, setMeasSecError] = useState(false);

  const isTableCellError = useCallback(
    (sourceColumn: string, fieldKey: TFieldTableCellKey): boolean =>
      Boolean(tableFieldErrors[sourceColumn]?.[fieldKey]),
    [tableFieldErrors],
  );

  const clearTableCellError = useCallback(
    (sourceColumn: string, fieldKey: TFieldTableCellKey): void => {
      setTableFieldErrors((prev) => {
        if (!prev[sourceColumn]?.[fieldKey]) {
          return prev;
        }
        const row = { ...prev[sourceColumn] };
        delete row[fieldKey];
        const next = { ...prev };
        if (Object.keys(row).length === 0) {
          delete next[sourceColumn];
        } else {
          next[sourceColumn] = row;
        }
        return next;
      });
    },
    [],
  );

  const clearFieldsValueError = useCallback(() => {
    setFieldsValueError(false);
  }, []);

  const clearDimSecError = useCallback(() => {
    setDimSecError(false);
  }, []);

  const clearMeasSecError = useCallback(() => {
    setMeasSecError(false);
  }, []);

  const validateOnNext = useCallback(
    (fieldConfig: TFieldConfigStepValidateInput): boolean => {
      const result = validateFieldConfigStepOnConfirm(fieldConfig);
      setTableFieldErrors(result.cellErrors);
      setFieldsValueError(result.noFieldsSelected);
      setDimSecError(result.dimSecError);
      setMeasSecError(result.measSecError);
      if (!result.valid) {
        const msgId = resolveStep2ValidateNotifyMsgId(result);
        if (msgId) {
          notification.warning({
            description: formatMessage({ id: msgId }),
          });
        }
      }
      return result.valid;
    },
    [formatMessage],
  );

  return {
    isTableCellError,
    clearTableCellError,
    fieldsValueError,
    dimSecError,
    measSecError,
    validateOnNext,
    clearFieldsValueError,
    clearDimSecError,
    clearMeasSecError,
  };
};
